import { useState, useCallback } from 'react';
import Token from 'src/util/Token';

/**
 * Custom hook for handling file downloads
 * @param {string} queryKey - The query key for the download endpoint
 * @param {object} options - Additional options for the download
 * @returns {object} - Download state and methods
 */
const useDownload = (queryKey, options = {}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const download = useCallback(async (requestBody, customOptions = {}) => {
    setIsDownloading(true);
    setDownloadError(null);

    try {
      const {
        fileName,
        fileExtension,
        onSuccess,
        onError,
        ...fetchOptions
      } = { ...options, ...customOptions };

      // Get the endpoint URL from the query key
      const endpoint = queryKey;
      
      // Make the request using fetch for file downloads
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${Token.get("_flst")}`,
          ...fetchOptions.headers,
        },
        body: JSON.stringify(requestBody),
        ...fetchOptions,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Download failed: ${response.status} ${response.statusText} - ${errorText}`);
      }

      // Get the blob data
      const blob = await response.blob();

      // Generate filename if not provided
      const generatedFileName = fileName || 
        `${requestBody.reportType || 'download'}_${new Date().toISOString().split('T')[0]}.${fileExtension || 'csv'}`;

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = generatedFileName;
      link.style.display = 'none';
      
      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      }, 100);

      // Call success callback if provided
      if (onSuccess) {
        onSuccess(blob, generatedFileName);
      }

      return { success: true, blob, fileName: generatedFileName };

    } catch (error) {
      console.error('Download error:', error);
      setDownloadError(error.message);
      
      // Call error callback if provided
      if (customOptions.onError || options.onError) {
        (customOptions.onError || options.onError)(error);
      }

      return { success: false, error: error.message };
    } finally {
      setIsDownloading(false);
    }
  }, [queryKey, options]);

  const clearError = useCallback(() => {
    setDownloadError(null);
  }, []);

  return {
    download,
    isDownloading,
    downloadError,
    clearError,
  };
};

export default useDownload;
